/* eslint-disable react/forbid-prop-types */
// eslint-disable-next-line no-unused-vars
import { h } from 'preact';
import PropTypes from 'prop-types';
import { useEffect, useReducer, useCallback } from 'preact/hooks';
import queryString from 'query-string';
import { SearchContext, reducer, initialState } from '../../contexts/Search';
import { emitEvent } from '../../util';

const { speak } = wp.a11y;
const { __, sprintf } = wp.i18n;

// Our REST endpoint.
// Comes from PHP localized variable.
const { endpoint, i18n, postId, postTab, locale } = window.MSRSearch;
let { initialLoadResults } = window.MSRSearch;

/**
 * The search provider component.
 *
 * @param {Object} props The component props.
 * @return {Object} JSX
 */
const SearchProvider = ( props ) => {
	const { children, initialParams, sectionName } = props;
	const [state, dispatch] = useReducer( reducer, initialState );

	/**
	 * Updates various attributes of the response with global state.
	 *
	 * @param {Object} value The parsed response object from our call.
	 */
	const setResponse = ( value ) => {
		dispatch( {
			type: 'SET_RESPONSE',
			payload: {
				value,
			},
		} );
	};

	/**
	 * Set the loading state in the global state.
	 *
	 * @param {boolean} value True if loading, false if not.
	 */
	const setLoading = ( value ) => {
		dispatch( {
			type: 'SET_LOADING',
			payload: {
				value,
			},
		} );
	};

	/**
	 * Set the params in the global state.
	 *
	 * @param {Object} value The updated params object to set.
	 */
	const setParams = ( value ) => {
		dispatch( {
			type: 'SET_PARAMS',
			payload: {
				value,
			},
		} );
	};

	/**
	 * Set the sectionName in the global state.
	 *
	 * @param {Object} value The updated params object to set.
	 */
	const setSectionName = ( value ) => {
		dispatch( {
			type: 'SET_SECTION_NAME',
			payload: {
				value,
			},
		} );
	};

	/**
	 * Get status message that appears at the top of the header.
	 * Example: "Showing 1 of 20 posts. Current filters: {taxonomy}"
	 *
	 * @param {Object} options Options object.
	 * @param {Object} options.params Updated params.
	 * @param {Int} options.postsPerPage Posts per page.
	 * @param {number} options.foundPosts Number of found posts.
	 * @param {Array} options.currentSelections Array of current filter selections.
	 * @param {boolean} options.short True to get short version, false to get long version.
	 * @param {Int} options.page The current page.
	 * @return {string} The updated status message.
	 */
	const getMessage = ( options ) => {

		const defaults = {
			params: {},
			postsPerPage: 10,
			foundPosts: 0,
			currentSelections: [],
			short: true,
			page: 1
		};

		const settings = Object.assign( {}, defaults, options );

		const {
			params,
			postsPerPage,
			foundPosts,
			currentSelections,
			short,
			page
		} = settings;

		const hasSearchQuery = params.q && params.q.length;
		const startingNumber = ( page - 1 ) * postsPerPage + 1;
		let endingNumber = page * postsPerPage;
		let message = '';

		if ( endingNumber > foundPosts ) {
			endingNumber = foundPosts;
		}

		// Pretty format output for numbers:
		const fmtEndingNumber = endingNumber.toLocaleString('en-US');
		const fmtFoundPosts = foundPosts.toLocaleString('en-US');

		// TODO this needs translating
		message = hasSearchQuery
			? sprintf(
				/* translators: 1: starting number, 2: ending number, 3: found posts, 4: search query */
				__( 'Showing %1$s - %2$s of %3$s results for “%4$s”', 'ms-research' ),
				startingNumber,
				fmtEndingNumber,
				fmtFoundPosts,
				params.q
			)
			: sprintf(
				/* translators: 1: starting number, 2: ending number, 3: found posts */
				__( 'Showing %1$s - %2$s of %3$s results', 'ms-research' ),
				startingNumber,
				fmtEndingNumber,
				fmtFoundPosts
			);

		if ( currentSelections.length && !short ) {
			message += __( '. Current filters: ', 'ms-research' );
			const selections = currentSelections.map( ( { label } ) => label );
			message += `${selections.join( ', ' )}.`;
		}

		return message;
	};

	/**
	 *
	 * @param {*} params
	 * @returns
	 */
	const getCurrentUrl = ( params ) => {
		// Make a copy of the params
		const paramsCopy = { ...params };

		// Remove the page key.
		// This is because in the actual URL, "pg" is used, and we don't want both.
		delete paramsCopy.page;

		// But, we also want to use that page if supplied.
		// Remap it.
		if ( params.page ) {
			paramsCopy.pg = params.page;
		}

		// Clean out empty values so we don't generate a trailing question mark or keys like q=
		const cleaned = {};
		Object.entries( paramsCopy ).forEach( ( [ key, value ] ) => {
			if ( value === '' || value === null || value === undefined ) {
				return;
			}
			if ( Array.isArray( value ) && value.length === 0 ) {
				return;
			}
			cleaned[ key ] = value;
		} );

		// Make a clean query string only if something remains.
		const query = queryString.stringify( cleaned, { arrayFormat: 'bracket' } ).replaceAll( '%5B%5D[]', '%5B%5D' );

		// Get window path info.
		const { origin, pathname } = window.location;

		// Put it all together.
		return query ? `${origin}${pathname}?${query}` : `${origin}${pathname}`;
	};

	/**
	 * Main function of the provider. Initializes an API call and handles all state updates before, during, and after.
	 * Creates a query for the API based on the parameters object passed.
	 *
	 * @param {Object} params Updated params to send to the API.
	 * @param {boolean} append Append the data to the results or start over. Default false.
	 * @param {boolean} focus Focus the first new result in the response. Default false.
	 */
	const update = useCallback( async ( params, append = false, focus = false, popstate = false ) => {

		// Do not force an empty `q` param; keep URL pristine until a real search/filter occurs.
		// Track whether this is the very first load (initialLoadResults primed) so we can suppress the initial auto-scroll.
		const wasInitialLoad = false !== initialLoadResults;

		// Set loading so we can update the UI
		setLoading( true );

		setSectionName( sectionName );

		// Let screen reader users know we're getting more posts
		speak( i18n.loadingStart, 'assertive' );

		// Build request URL.
		let response = {};
		const queryParams = {
			...params,
			'post_id': postId,
			'post_tab': postTab,
			'locale': locale,
		};
		const query = queryString.stringify( queryParams, { arrayFormat: 'bracket' } ).replaceAll( '%5B%5D[]', '%5B%5D' );
		const url = `${endpoint}?${query}`;

		// Send analytics
		emitEvent( 'preact:submit' );

		emitEvent( 'preact:requeststart' );


		// Any additional settings.
		// Blank for now since we don't need them.
		const settings = {};

		if ( false !== initialLoadResults ) {
			response = initialLoadResults;
			initialLoadResults = false;
		} else {
			// Make the request and put it in the variable we set earlier.
			response = await fetch( url, settings )
				.then( ( res ) => {
					// Check response.
					if ( !res.ok ) {
						// There was an error
						return Promise.reject( res );
					}
					// Return our JSON if we're OK.
					// Note - If there is any logging printed on page WP, this
					// will fail with JS error due to incorrect response format (html tags).
					return res.json();
				} )
				.catch( ( error ) => {
					// eslint-disable-next-line no-console
					console.warn( error );
				} );
		}

		emitEvent( 'preact:requestend' );

		// Map the keys from the response.
		const {
			current_selections: currentSelections,
			facets,
			found_posts: foundPosts,
			max_num_pages: maxNumPages,
			posts,
			users,
			posts_per_page: postsPerPage,
			filter_queries: filterQueries
		} = response;

		// Update response states
		setResponse( {
			currentSelections,
			facets,
			foundPosts,
			maxNumPages,
			postsPerPage,
			users,
			filterQueries
		} );

		// Set page
		dispatch( {
			type: 'SET_PAGE',
			payload: {
				value: response.page,
			},
		} );

		// Sync params
		setParams( params );

		// Update focus
		dispatch( {
			type: 'SET_FOCUS_NEW_POST',
			payload: { value: focus },
		} );

		// Update posts
		dispatch( {
			type: 'SET_POSTS',
			payload: {
				value: posts,
				append,
			},
		} );

		const currentUrl = getCurrentUrl( params );

		// Update the window history. Maybe.
		if ( !popstate ) {
			const title = '';

			window.history.pushState( { params, url: currentUrl }, title, currentUrl );
		} else {
			// So we can keep track of param changes.
			window.history.replaceState( { params, url: currentUrl }, document.title, currentUrl );
		}

		// Set loading so we can update the UI
		setLoading( false );

		// Scroll to the top of the container
		const container = document.getElementById( 'faceted-search' );
		// Skip scrolling on the first render so we don't jump the user past the header.
		if ( container && !wasInitialLoad ) {
			const elementPosition = container.getBoundingClientRect().top;
			const offsetPosition = elementPosition + window.pageYOffset - 60;

			window.scrollTo( {
				top: offsetPosition,
				behavior: 'smooth',
			} );
		}

		// Let screen reader users know loading has finished.
		speak( i18n.loadingEnd, 'assertive' );

		// Also let them know what we found.
		speak(
			getMessage( {
				params,
				postsPerPage,
				foundPosts,
				currentSelections,
				page: response.page,
				short: false
			} ),
			'assertive'
		);

		// Always set the load more to false.
		dispatch( {
			type: 'SET_LOAD_MORE',
			payload: {
				value: false,
			},
		} );

		emitEvent( 'preact:end' );
	}, [] );

	/**
	 * Handle window popstate callback.
	 */
	const handleWindowPopstate = useCallback( () => {
		const { params } = window.history.state;

		update( params, false, false, true );
	}, [update] );

	/**
	 * Helper functions to pass.
	 */
	const helpers = {
		update,
		getMessage,
	};

	/**
	 * Initial load update.
	 */
	useEffect( () => {
		const updatedParams = { ...initialParams };

		update( updatedParams, false, false, true );
	}, [update, initialParams] );

	/**
	 * Handle popstate events.
	 * Bind on inital load.
	 */
	useEffect( () => {
		window.addEventListener( 'popstate', handleWindowPopstate );
	}, [handleWindowPopstate] );

	useEffect( () => {
		emitEvent( 'preact:contentchange' );
	} );

	return (
		<SearchContext.Provider value={{ state, dispatch, helpers }}>
			{children}
		</SearchContext.Provider>
	);
};

SearchProvider.propTypes = {
	children: PropTypes.array.isRequired,
	initialParams: PropTypes.object.isRequired,
};

export default SearchProvider;
